import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ToastService } from '../../modules/shared/services/toast-message.service';
import { EApiErrorCode, IApiError } from '../models/api-error';
import { AuthRedirectService } from '../services/auth-redirect.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private toastr: ToastService,
    private authRedirect: AuthRedirectService,
    private router: Router,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: any) => {
        // Access token rejected by the API (expired / rotated session). Auth0's
        // AuthGuard only checks the SDK session, so an expired *access token* slips
        // past it and would otherwise leave the app stuck — re-authenticate instead.
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          !this.authRedirect.isPublicUrl(req.url) &&
          this.authRedirect.redirectToLogin()
        ) {
          return EMPTY; // swallow — we're navigating away to log in
        }

        const apiError: IApiError | null =
          error instanceof HttpErrorResponse ? error.error : null;

        // Signed in to Auth0 but never invited (or the invitation expired). The API
        // refuses the very first request after login. Sign them out completely and show
        // the invitation-only screen, so to them it reads as a refused sign-in rather
        // than an app that let them in and then failed.
        if (apiError?.errorCode === EApiErrorCode.INVITATION_REQUIRED) {
          this.authRedirect.signOutAsAccessDenied();
          return EMPTY;
        }

        // A plan boundary is not a failure: the request was well formed and the answer is
        // "your plan does not include this". Warn in the same amber the plan locks use,
        // with the API's own wording, rather than reporting an error the customer cannot
        // act on. The UI hides these actions where it can, so reaching here means a race,
        // a stale view or a direct URL — all cases where the reason matters.
        // The tenant is not usable at all: TenantAccessMiddleware answers this on every
        // route, so toasting it would stack one message per in-flight request behind an
        // app that can never load. Send them to the screen that explains it instead, and
        // swallow the error so nothing downstream reports a failure the customer cannot
        // act on. Navigating only when not already there keeps concurrent 403s from
        // re-entering the route.
        if (
          apiError?.errorCode === EApiErrorCode.TENANT_DEACTIVATED ||
          apiError?.errorCode === EApiErrorCode.TENANT_SUSPENDED
        ) {
          const reason =
            apiError.errorCode === EApiErrorCode.TENANT_DEACTIVATED
              ? 'deactivated'
              : 'suspended';
          if (!this.router.url.startsWith('/account-unavailable')) {
            this.router.navigate(['/account-unavailable'], {
              queryParams: { reason },
            });
          }
          return EMPTY;
        }

        if (apiError?.errorCode === EApiErrorCode.PLAN_UPGRADE_REQUIRED) {
          const message =
            apiError.errorMessage ?? 'Your plan does not include this feature.';
          this.toastr.warning(message);
          return throwError(() => new Error(message));
        }

        // Not a failure either: the customer already asked for the upgrade (another tab,
        // a stale page) and the request is being handled. Say so quietly and hand the
        // original response on, so the caller can read the case number and flip its view.
        if (apiError?.errorCode === EApiErrorCode.PLAN_CHANGE_ALREADY_REQUESTED) {
          this.toastr.info(
            apiError.errorMessage ??
              'You have already requested an upgrade; we will be in touch.',
          );
          return throwError(() => error);
        }

        let errorMessage = '';

        if (error.error instanceof ErrorEvent) {
          errorMessage = error.error.message; // handle client-side or network errors
        } else {
          errorMessage = error.error?.errorMessage || error.message; // handle server-side errors
        }

        if (error?.name === 'CanceledError') {
          return throwError(() => error);
        }

        if (errorMessage !== 'Login required') {
          this.toastr.error(errorMessage ?? 'Something went wrong'); // show toast notification for the error
        }

        return throwError(() => new Error(errorMessage)); // return an observable with an error message
      }),
    );
  }
}
